import { PhpArray, PhpFloat, normalizeKey } from '../php-compat/ordered-array';
import { utf8Bytes } from './php-json-serialize';

/** igbinary v2's PHP array/scalar encoding (compact_strings=On).
 * https://github.com/igbinary/igbinary/blob/master/src/php7/igbinary.c */
export function serializeIgbinary(value: unknown): number[] {
    const bytes = [0, 0, 0, 2], strings = new Map<string, number>(), arrays = new Map<object, number>();
    let references = 0;
    const unsigned = (value: bigint, width: number): void => {
        for (let i = width - 1; i >= 0; i--) bytes.push(Number(value >> BigInt(i * 8) & 255n));
    };
    const unsigned32 = (value: number, width: number): void => {
        for (let i = width - 1; i >= 0; i--) bytes.push(value >>> (i * 8) & 255);
    };
    const sized = (value: number, tag: number): void => {
        const width = value <= 255 ? 1 : value <= 65535 ? 2 : 4;
        bytes.push(tag + (width === 1 ? 0 : width === 2 ? 1 : 2)); unsigned32(value, width);
    };
    function encode(value: unknown): void {
        if (value === null) { bytes.push(0); return; }
        if (typeof value === 'boolean') { bytes.push(value ? 5 : 4); return; }
        if (typeof value === 'string') {
            if (!value.length) { bytes.push(13); return; }
            const known = strings.get(value);
            if (known !== undefined) { sized(known, 14); return; }
            strings.set(value, strings.size);
            const text = utf8Bytes(value); sized(text.length, 17);
            for (const byte of text) bytes.push(byte);
            return;
        }
        if (value instanceof PhpFloat || typeof value === 'number' && (!Number.isInteger(value) || Object.is(value, -0)
            || value >= 9223372036854775808 || value < -9223372036854775808)) {
            const buffer = new ArrayBuffer(8); new DataView(buffer).setFloat64(0, value instanceof PhpFloat ? value.value : value as number);
            bytes.push(12, ...new Uint8Array(buffer)); return;
        }
        if (typeof value === 'number' || typeof value === 'bigint') {
            if (typeof value === 'number' && Math.abs(value) <= 4294967295) {
                const magnitude = Math.abs(value), width = magnitude <= 255 ? 1 : magnitude <= 65535 ? 2 : 4;
                bytes.push((width === 4 ? 10 : width === 2 ? 8 : 6) + (value < 0 ? 1 : 0));
                unsigned32(magnitude, width); return;
            }
            const n = BigInt(value), magnitude = n < 0 ? -n : n;
            if (n < -9223372036854775808n || n > 9223372036854775807n) throw new RangeError('PHP integer exceeds 64 bits');
            const width = magnitude <= 255n ? 1 : magnitude <= 65535n ? 2 : magnitude <= 4294967295n ? 4 : 8;
            bytes.push((width === 8 ? 32 : width === 4 ? 10 : width === 2 ? 8 : 6) + (n < 0 ? 1 : 0));
            unsigned(magnitude, width); return;
        }
        if (typeof value !== 'object') throw new TypeError('igbinary requires PHP array/scalar values');
        const entries = value instanceof PhpArray ? [...value] : Object.entries(value);
        // A repeated array identity emits a backreference, including PHP's
        // shared literal empty array (distinct empty allocations still advance).
        const known = arrays.get(value);
        if (known !== undefined) { sized(known, 1); return; }
        arrays.set(value, references++);
        sized(entries.length, 20);
        for (const [key, child] of entries) { encode(normalizeKey(key)); encode(child); }
    }
    encode(value); return bytes;
}
